import React from 'react';
import { Header, Icon, Message, Button, Grid, Form, Segment, Label } from 'semantic-ui-react';
import { formatCpfProgressive, cleanCpf, normalizeRepresentativePhoneInput, resolveCompleteCpfFromPartnerSource, isPartnerCpfMaskedOrIncomplete, isValidCpfLength } from '../../services/utils/stringUtils';
import type { ContractRepresentanteFormItem } from '../../types/contractForm';
import type { ClientResponse, ClientPartnerResponse } from '../../types/dtos';

export type RepresentanteManualState = {
  nome: string;
  cpf: string;
  rg: string;
  email: string;
  telefone: string;
};

const isValidRepresentativeEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const phoneDigitsCount = (v: string) => normalizeRepresentativePhoneInput(v).length;

export interface ContractFormRepresentativesTabProps {
  representantes: ContractRepresentanteFormItem[];
  setRepresentantes: React.Dispatch<React.SetStateAction<ContractRepresentanteFormItem[]>>;
  representanteManual: RepresentanteManualState;
  setRepresentanteManual: React.Dispatch<React.SetStateAction<RepresentanteManualState>>;
  selectedClient: ClientResponse | null | undefined;
  clientPartners: ClientPartnerResponse[];
}

export const ContractFormRepresentativesTab: React.FC<ContractFormRepresentativesTabProps> = ({
  representantes,
  setRepresentantes,
  representanteManual,
  setRepresentanteManual,
  selectedClient,
  clientPartners,
}) => {
  const addPartnerAsRepresentante = (partner: ClientPartnerResponse) => {
    const completeCpf = resolveCompleteCpfFromPartnerSource(partner.cpfCnpj);
    const maskedOrIncomplete = isPartnerCpfMaskedOrIncomplete(partner.cpfCnpj);
    // Não usar partner.id como id do ContractRepresentative — no PATCH a API trata Guid
    // existente como update e falha em silêncio (não cria o QSA novo).
    if (
      representantes.some(
        (rep) =>
          rep.id === `partner-${partner.id}` ||
          (completeCpf.length === 11 && cleanCpf(rep.cpf) === completeCpf)
      )
    ) {
      return;
    }
    setRepresentantes((prev) => [
      ...prev,
      {
        id: `partner-${partner.id}`,
        nome: partner.nome,
        qualificacao: partner.qualificacao,
        // CPF mascarado da Receita não entra como valor — usuário completa os 11 dígitos.
        cpf: completeCpf,
        cpfHintFromQsa: maskedOrIncomplete ? partner.cpfCnpj?.trim() || undefined : undefined,
        rg: partner.rg,
        email: '',
        telefone: '',
        manual: false,
      },
    ]);
  };

  const addManualRepresentante = () => {
    const nome = representanteManual.nome.trim();
    const cpf = representanteManual.cpf.trim();
    const email = representanteManual.email.trim();
    const telefone = normalizeRepresentativePhoneInput(representanteManual.telefone);
    if (!nome || !cpf || !email || !telefone) return;
    if (!isValidRepresentativeEmail(email)) return;
    const d = telefone.length;
    if (d !== 10 && d !== 11) return;

    const novoRepresentante: ContractRepresentanteFormItem = {
      id: `manual-${Date.now()}`,
      nome,
      qualificacao: 'Representante',
      cpf,
      rg: representanteManual.rg.trim() || undefined,
      email,
      telefone,
      manual: true,
    };
    setRepresentantes((prev) => [...prev, novoRepresentante]);
    setRepresentanteManual({ nome: '', cpf: '', rg: '', email: '', telefone: '' });
  };

  const manualFormValid =
    representanteManual.nome.trim() &&
    representanteManual.cpf.trim() &&
    representanteManual.email.trim() &&
    isValidRepresentativeEmail(representanteManual.email) &&
    representanteManual.telefone.trim() &&
    (phoneDigitsCount(representanteManual.telefone) === 10 ||
      phoneDigitsCount(representanteManual.telefone) === 11);

  return (
    <div style={{ padding: '20px' }}>
      <Header as="h3" style={{ color: '#2c3e50', marginBottom: '20px' }}>
        <Icon name="users" />
        Representante Legal da Empresa (QSA)
      </Header>

      <Message info style={{ marginBottom: '20px', backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
        <Message.Header>
          <Icon name="building" />
          Quem são os Representantes Legais da Empresa?
        </Message.Header>
        <p style={{ fontSize: '14px', lineHeight: '1.6', marginTop: '10px' }}>
          <strong>Os Representantes Legais são os QSA (Quadro de Sócios e Administradores) da empresa</strong>, ou
          seja, os <strong style={{ color: '#2e7d32' }}>sócios, donos ou administradores</strong> do CNPJ.
        </p>
        <p style={{ fontSize: '14px', lineHeight: '1.6', marginTop: '8px', marginBottom: '0' }}>
          Estes são os responsáveis legais pela empresa e devem assinar o contrato. Você pode selecionar os
          QSA/Partners cadastrados no cliente ou adicionar manualmente.
        </p>
        <p style={{ fontSize: '14px', lineHeight: '1.6', marginTop: '10px', marginBottom: '0' }}>
          <strong>E-mail e telefone de cada QSA são obrigatórios</strong> e aparecem na{' '}
          <strong>proposta comercial</strong> do contrato (próximo à assinatura do representante legal).
        </p>
      </Message>

      {representantes.length > 0 && (
        <Segment style={{ marginBottom: '20px' }}>
          <Header as="h4" style={{ color: '#2c3e50' }}>
            <Icon name="users" />
            Representantes Legais Selecionados ({representantes.length})
          </Header>
          {representantes.map((rep, index) => (
            <div
              key={rep.id}
              style={{
                padding: '15px',
                borderBottom: index < representantes.length - 1 ? '1px solid #eee' : 'none',
                backgroundColor: '#f8f9fa',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>{rep.nome}</strong>
                    {rep.manual ? (
                      <Label size="mini" color="orange">
                        Manual
                      </Label>
                    ) : (
                      <Label size="mini" color="blue">
                        QSA/Partner
                      </Label>
                    )}
                  </div>
                  <br />
                  <small style={{ color: '#666' }}>{rep.qualificacao || 'Sem qualificação'}</small>
                </div>
                <Button
                  size="small"
                  color="red"
                  icon="trash"
                  onClick={() => setRepresentantes((prev) => prev.filter((r) => r.id !== rep.id))}
                />
              </div>

              <Form.Field required style={{ marginTop: '10px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>CPF</label>
                <input
                  type="text"
                  placeholder="000.000.000-00"
                  value={formatCpfProgressive(rep.cpf || '')}
                  onChange={(e) => {
                    const cleanedValue = cleanCpf(e.target.value);
                    setRepresentantes((prev) =>
                      prev.map((r) => (r.id === rep.id ? { ...r, cpf: cleanedValue } : r))
                    );
                  }}
                  maxLength={14}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    borderColor: !isValidCpfLength(rep.cpf || '') ? '#ff6b6b' : '#ccc',
                  }}
                />
                {rep.cpfHintFromQsa ? (
                  <div style={{ fontSize: '11px', color: '#b45309', marginTop: '4px', fontWeight: 600 }}>
                    A Receita retornou CPF mascarado ({rep.cpfHintFromQsa}). Informe o CPF completo (11 dígitos).
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    Aceita CPF com ou sem formatação (11 dígitos)
                  </div>
                )}
                {!isValidCpfLength(rep.cpf || '') && (
                  <small style={{ color: '#ff6b6b', fontSize: '11px' }}>
                    CPF completo obrigatório para este representante
                  </small>
                )}
              </Form.Field>

              <Form.Field required style={{ marginTop: '10px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>E-mail (proposta comercial)</label>
                <input
                  type="email"
                  placeholder="email@empresa.com.br"
                  value={rep.email || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRepresentantes((prev) =>
                      prev.map((r) => (r.id === rep.id ? { ...r, email: v } : r))
                    );
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    borderColor: !rep.email?.trim() || !isValidRepresentativeEmail(rep.email || '') ? '#ff6b6b' : '#ccc',
                  }}
                />
                {!rep.manual && (
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    Não vem do QSA da Receita — preencha o e-mail do representante.
                  </div>
                )}
              </Form.Field>

              <Form.Field required style={{ marginTop: '10px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>Telefone (proposta comercial)</label>
                <input
                  type="text"
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder="41984028564"
                  aria-label="Telefone do representante, apenas números, DDD e número"
                  value={rep.telefone || ''}
                  onChange={(e) => {
                    const v = normalizeRepresentativePhoneInput(e.target.value);
                    setRepresentantes((prev) =>
                      prev.map((r) => (r.id === rep.id ? { ...r, telefone: v } : r))
                    );
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    borderColor:
                      !rep.telefone?.trim() ||
                      (phoneDigitsCount(rep.telefone || '') !== 10 && phoneDigitsCount(rep.telefone || '') !== 11)
                        ? '#ff6b6b'
                        : '#ccc',
                  }}
                />
                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  {!rep.manual
                    ? 'Não vem do QSA da Receita — preencha o telefone (10 ou 11 dígitos).'
                    : 'Apenas números — 10 (fixo) ou 11 dígitos (celular). Ex.: 41984028564'}
                </div>
              </Form.Field>
            </div>
          ))}
        </Segment>
      )}

      {representantes.length === 0 && (
        <Message warning style={{ marginBottom: '20px' }}>
          <Message.Header>
            <Icon name="warning sign" />
            Atenção
          </Message.Header>
          <p>
            Você precisa adicionar pelo menos um <strong>Representante Legal</strong> (QSA da empresa). Selecione os
            QSA/Partners disponíveis abaixo ou cadastre manualmente.
          </p>
        </Message>
      )}

      <Segment>
        <Header as="h4" style={{ color: '#2c3e50', marginBottom: '15px' }}>
          <Icon name="search" />
          Adicionar Representante Legal (QSA da Empresa)
        </Header>

        {selectedClient && clientPartners.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              <Icon name="users" />
              QSA/Partners disponíveis (Sócios e Administradores da Empresa):
            </label>
            <div
              style={{
                maxHeight: '150px',
                overflowY: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginBottom: '15px',
              }}
            >
              {clientPartners
                .filter((partner) => {
                  const completeCpf = resolveCompleteCpfFromPartnerSource(partner.cpfCnpj);
                  return !representantes.some(
                    (rep) =>
                      rep.id === `partner-${partner.id}` ||
                      (completeCpf.length === 11 && cleanCpf(rep.cpf) === completeCpf)
                  );
                })
                .map((partner) => (
                  <div
                    key={partner.id}
                    style={{
                      padding: '8px',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    onClick={() => addPartnerAsRepresentante(partner)}
                  >
                    <div>
                      <strong>{partner.nome}</strong>
                      <br />
                      <small style={{ color: '#666' }}>{partner.qualificacao || 'Sem qualificação'}</small>
                    </div>
                    <Icon name="plus" color="green" />
                  </div>
                ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            <Icon name="edit" />
            Ou cadastre manualmente um QSA/Representante Legal:
          </label>

          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={6}>
                <Form.Field required>
                  <label>Nome Completo</label>
                  <input
                    type="text"
                    placeholder="Digite o nome completo"
                    value={representanteManual.nome}
                    onChange={(e) => setRepresentanteManual((prev) => ({ ...prev, nome: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                </Form.Field>
              </Grid.Column>

              <Grid.Column width={5}>
                <Form.Field required>
                  <label>CPF</label>
                  <input
                    type="text"
                    placeholder="000.000.000-00"
                    value={formatCpfProgressive(representanteManual.cpf)}
                    onChange={(e) => {
                      const cleanedValue = cleanCpf(e.target.value);
                      setRepresentanteManual((prev) => ({ ...prev, cpf: cleanedValue }));
                    }}
                    maxLength={14}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Máximo 11 dígitos</div>
                </Form.Field>
              </Grid.Column>

              <Grid.Column width={5}>
                <Form.Field>
                  <label>RG (opcional)</label>
                  <input
                    type="text"
                    placeholder="0000000000"
                    value={representanteManual.rg}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').substring(0, 10);
                      setRepresentanteManual((prev) => ({ ...prev, rg: value }));
                    }}
                    maxLength={10}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Máximo 10 dígitos (opcional)</div>
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={8}>
                <Form.Field required>
                  <label>E-mail (proposta comercial)</label>
                  <input
                    type="email"
                    placeholder="email@empresa.com.br"
                    value={representanteManual.email}
                    onChange={(e) => setRepresentanteManual((prev) => ({ ...prev, email: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={8}>
                <Form.Field required>
                  <label>Telefone (proposta comercial)</label>
                  <input
                    type="text"
                    inputMode="tel"
                    autoComplete="tel-national"
                    placeholder="41984028564"
                    aria-label="Telefone manual do representante, apenas números"
                    value={representanteManual.telefone}
                    onChange={(e) =>
                      setRepresentanteManual((prev) => ({
                        ...prev,
                        telefone: normalizeRepresentativePhoneInput(e.target.value),
                      }))
                    }
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    Apenas números — 10 ou 11 dígitos. Ex.: 41984028564
                  </div>
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={16}>
                <Button color="blue" onClick={addManualRepresentante} disabled={!manualFormValid}>
                  <Icon name="plus" />
                  Adicionar Representante Manual
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Segment>

      {!selectedClient && (
        <Message warning>
          <Icon name="warning sign" />
          Selecione um cliente primeiro para acessar os QSA/Partners disponíveis.
        </Message>
      )}

      {selectedClient && clientPartners.length === 0 && (
        <Message info>
          <Icon name="info circle" />
          Nenhum QSA/Partner encontrado para o cliente selecionado.
        </Message>
      )}

      <Message warning style={{ marginTop: '15px' }}>
        <Message.Header>
          <Icon name="exclamation triangle" />
          Importante
        </Message.Header>
        <p style={{ marginTop: '8px' }}>
          <strong>Os Representantes Legais são os QSA (Quadro de Sócios e Administradores) da empresa</strong>, ou seja,
          os sócios, donos ou administradores do CNPJ que devem assinar o contrato.
        </p>
        <p style={{ marginTop: '8px', marginBottom: '0' }}>
          O <strong>e-mail e o telefone</strong> de cada representante são exibidos na <strong>proposta comercial</strong>{' '}
          e devem ser os dados de contato do QSA que assina, não os do responsável pela auditoria (aba anterior).
        </p>
      </Message>
    </div>
  );
};
